import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { TrendUpIcon, SpinnerGapIcon, UsersThreeIcon, LockKeyIcon, FireIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchWalletBuys, fetchSmartMoneyConfig } from "../lib/api";
import { TradeButton } from "../components/TradeButton";
import { CreatorRugBadge, RuggedButton } from "../components/RugControls";
import { AlphaBadge, useAlpha } from "../components/AlphaBadge";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { useAlphaChime } from "../lib/alphaSound";
import { shortAddr, timeAgo } from "../lib/solana";

const Fallback = ({ symbol }) => (
  <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground shrink-0">
    {(symbol || "?").slice(0, 3)}
  </div>
);

function BuyCard({ token }) {
  const coin = { address: token.tokenMint, symbol: token.tokenSymbol, creator: token.creator, url: `https://dexscreener.com/solana/${token.tokenMint}` };
  const hot = token.walletCount >= 3;
  const alpha = useAlpha()(token.creator);
  return (
    <div data-testid={`buy-card-${token.tokenMint}`} className={`border bg-card p-4 space-y-3 ${alpha ? "border-[#C9A227]/50 bg-[#C9A227]/[0.06]" : hot ? "border-warn/40" : "border-white/10"}`}>
      <div className="flex items-start gap-3">
        {token.tokenImage ? (
          <img src={token.tokenImage} alt={token.tokenSymbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Fallback symbol={token.tokenSymbol} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading font-bold text-base truncate">{token.tokenSymbol || shortAddr(token.tokenMint)}</div>
            <AlphaBadge creator={token.creator} compact />
            {hot && (
              <span data-testid={`conviction-badge-${token.tokenMint}`} className="flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 bg-warn/15 text-warn border border-warn/30 shrink-0">
                <FireIcon size={11} weight="fill" /> {token.walletCount} WALLETS
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{token.tokenName || shortAddr(token.tokenMint)}</div>
          <CreatorRugBadge creator={token.creator} className="mt-1" />
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(token.lastTimestamp)}</span>
          <RuggedButton mint={token.tokenMint} symbol={token.tokenSymbol} creator={token.creator} size={15} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center border-t border-white/5 pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallets</div>
          <div className={`font-mono font-bold text-sm mt-0.5 ${hot ? "text-warn" : "text-neon"}`}>{token.walletCount}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Buys</div>
          <div className="font-mono text-sm mt-0.5">{token.buyCount}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SOL In</div>
          <div className="font-mono text-sm mt-0.5">{token.totalSol}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {token.buyers.slice(0, 4).map((b) => (
          <span key={b.address} className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">
            {b.label || shortAddr(b.address)}
          </span>
        ))}
        {token.walletCount > 4 && <span className="font-mono text-[10px] px-2 py-1 text-muted-foreground">+{token.walletCount - 4}</span>}
      </div>

      <div className="pt-1">
        <TradeButton coin={coin} size="sm" />
      </div>
    </div>
  );
}

export default function WalletBuys() {
  const clientId = getClientId();
  const [hotOnly, setHotOnly] = useState(false);
  const configQ = useQuery({ queryKey: ["smConfig"], queryFn: fetchSmartMoneyConfig });
  const { data, isLoading } = useQuery({
    queryKey: ["walletBuys", clientId],
    queryFn: () => fetchWalletBuys(clientId),
    refetchInterval: 60000,
  });

  const allTokens = data?.tokens || [];
  const tokens = hotOnly ? allTokens.filter((t) => t.walletCount >= 3) : allTokens;
  const hotCount = allTokens.filter((t) => t.walletCount >= 3).length;
  const heliusConfigured = configQ.data?.heliusConfigured;

  const { isAlpha } = useTrustedCreators();
  useAlphaChime(allTokens.filter((t) => isAlpha(t.creator)).map((t) => t.tokenMint));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
              <TrendUpIcon size={24} weight="bold" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">WALLET BUYS</h1>
          </div>
          <p className="text-muted-foreground mt-3 text-sm">
            Live coins your <Link to="/smart-money" className="text-neon hover:underline">tracked wallets</Link> are buying — most recent first. One tap to buy the same coin.
          </p>
        </div>
        <button
          data-testid="conviction-filter-toggle"
          onClick={() => setHotOnly((v) => !v)}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border transition-colors ${
            hotOnly ? "bg-warn/15 border-warn/40 text-warn" : "border-white/15 text-muted-foreground hover:text-white hover:border-white/30"
          }`}
        >
          <FireIcon size={16} weight="fill" /> Bought by 3+ wallets{hotCount > 0 && <span className="font-mono text-xs opacity-70">({hotCount})</span>}
        </button>
      </div>

      {!configQ.isLoading && !heliusConfigured && (
        <div data-testid="helius-warning" className="border border-warn/30 bg-warn/5 p-4 flex gap-3">
          <LockKeyIcon size={20} className="text-warn shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">Wallet tracking isn't connected yet. Buys will appear once the app owner adds a Helius key.</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Loading recent buys…</span>
        </div>
      ) : tokens.length === 0 ? (
        <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center">
          <UsersThreeIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-md">
            {hotOnly && allTokens.length > 0
              ? "No coins bought by 3+ tracked wallets yet. Turn off the filter to see all buys."
              : "No buys yet. Track proven wallets on the Smart Money page — their buys show up here automatically."}
          </p>
          {!(hotOnly && allTokens.length > 0) && (
            <Link to="/smart-money" data-testid="walletbuys-go-sm" className="px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors">Track wallets</Link>
          )}
        </div>
      ) : (
        <div data-testid="buys-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tokens.map((t) => <BuyCard key={t.tokenMint} token={t} />)}
        </div>
      )}
    </div>
  );
}
